import type {ReactElement} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {ANALYTICS_ACTION_FOR_SOCIAL, DisplayType, ThumbNailType, ANALYTICS_ACTION_FOR_PUBLISH} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {ModalDetails} from '@Components/modal/modal-context';
import type {PublishingOptions} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {GA4EventParamName} from '@Libraries/ga-events';
import {useSocialMediaLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import styles from './publishing-social-media-panel.module.scss';
import type {PublishingSocialMediaPanelProps, SocialMediaResponse} from './publishing-social-media-panel.types';

export function PublishingSocialMediaPanel({...props}: PublishingSocialMediaPanelProps): ReactElement {
  const {displayType} = props;
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const [facebookLink, setFacebookLink] = useState('');
  const [pinterestLink, setPinterestLink] = useState('');
  const [twitterLink, setTwitterLink] = useState('');
  const [posterScreenURL, setPosterScreenURL] = useState('');
  const [posterName, setPosterName] = useState('');

  const socialMediaLinksForPublishing: SocialMediaResponse = useSocialMediaLinks();
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'SocialLink'});

  useEffect(() => {
    if (socialMediaLinksForPublishing) {
      setFacebookLink(socialMediaLinksForPublishing.fb);
      setPinterestLink(socialMediaLinksForPublishing.pt);
      setTwitterLink(socialMediaLinksForPublishing.tw);
      setPosterScreenURL(socialMediaLinksForPublishing.ptUrl || posterScreenURL); // Keep default if no URL in socialMediaLinks
      setPosterName(socialMediaLinksForPublishing.posterName);
    }
  }, [socialMediaLinksForPublishing]);

  const onShareToFacebook = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_SOCIAL, {[GA4EventParamName.TYPE]: 'FacebookShare'});

    window.PMW.fb
      .init()
      .then(() => {
        const FB = (window as any).FB;

        if (FB) {
          FB.ui({
            method: 'share',
            display: 'popup',
            href: facebookLink,
            caption: `PosterMyWall | ${posterName}`,
          });
        } else {
          alert(window.i18next.t('pmwjs_unable_to_contact_fb_wait_reload'));
        }
      })
      .catch(() => {
        alert(window.i18next.t('pmwjs_unable_to_contact_fb_wait_reload'));
      });
  };

  const shareToTwitter = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_SOCIAL, {
      [GA4EventParamName.TYPE]: 'TwitterShare',
    });

    window.open(
      `https://twitter.com/share?url=${encodeURIComponent(twitterLink)}&related=postermywall&text=${encodeURIComponent(posterName)}`,
      'sharer',
      'toolbar=0,status=0,width=626,height=436'
    );
  };

  const shareToPinterest = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_SOCIAL, {[GA4EventParamName.TYPE]: 'PinterestShare'});
    window.open(
      `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pinterestLink)}&media=${encodeURIComponent(posterScreenURL)}&description=${encodeURIComponent(posterName)}`,
      'sharer',
      'toolbar=0,status=0,width=626,height=436'
    );
  };

  const publishingOptionsSocialMedia: PublishingOptions[] = [
    {
      id: 1,
      iconName: 'icon-pinterest',
      heading: window.i18next.t('social_media_share_pinterest'),
      subheading: window.i18next.t('social_media_share_pinterest_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      customThumbnailClasses: 'bg-neutral-3',
      imgSrc: 'publishing-social-pinterest.png',
      onClick: shareToPinterest,
    },
    {
      id: 2,
      iconName: 'icon-fb-circle',
      heading: window.i18next.t('social_media_share_facebook'),
      subheading: window.i18next.t('social_media_share_facebook_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      customThumbnailClasses: 'bg-neutral-3',
      imgSrc: 'publishing-social-facebook.png',
      onClick: onShareToFacebook,
    },
    {
      id: 3,
      iconName: 'icon-twitter',
      heading: window.i18next.t('social_media_share_x'),
      subheading: window.i18next.t('social_media_share_x_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      customThumbnailClasses: 'bg-neutral-3',
      imgSrc: 'publishing-social-x.png',
      onClick: shareToTwitter,
    },
  ];

  let panelStyle = '';
  if (inMobile) {
    panelStyle = styles.handleSpacingDropDown;
  } else {
    panelStyle = displayType === DisplayType.MODAL ? styles.handleSpacingPanel : styles.handleSpacingDropDown;
  }

  return (
    <div className={panelStyle}>
      {publishingOptionsSocialMedia.map((option) => {
        return (
          <PublishingOptionListItem
            key={option.id}
            iconName={option.iconName}
            heading={option.heading}
            subheading={option.subheading}
            thumbNailType={option.thumbNailType}
            customThumbnailClasses={option.customThumbnailClasses}
            imgSrc={option.imgSrc}
            onClick={option.onClick}
          />
        );
      })}
    </div>
  );
}
